import React, { useState } from 'react';
import { X, Globe, Link2, Sparkles, AlertCircle, Clipboard } from 'lucide-react';
import { normalizeCsvUrl } from '../utils/urlHelper';

interface UrlLoadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadUrl: (url: string) => Promise<boolean>;
  currentUrl?: string | null;
}

export const UrlLoadModal: React.FC<UrlLoadModalProps> = ({
  isOpen,
  onClose,
  onLoadUrl,
  currentUrl,
}) => {
  const [urlInput, setUrlInput] = useState(currentUrl || '');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setErrorMessage('Please enter a valid HTTP or HTTPS URL.');
      return;
    }

    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      setErrorMessage('URL must start with http:// or https://');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const success = await onLoadUrl(trimmed);
    setIsLoading(false);

    if (success) {
      onClose();
    } else {
      setErrorMessage('Failed to fetch CSV from URL. If using Google Sheets, make sure access is set to "Anyone with the link can view".');
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setUrlInput(text.trim());
        setErrorMessage(null);
      }
    } catch (err) {
      console.warn('Could not read clipboard contents:', err);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.65)',
      backdropFilter: 'blur(6px)',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      animation: 'fadeIn 0.2s ease-out',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '540px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-color)',
        borderRadius: '16px',
        boxShadow: 'var(--shadow-xl)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-surface)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'var(--accent-glow)',
              color: 'var(--accent-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Globe size={20} />
            </div>
            <div>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-main)',
              }}>
                Load Public CSV / Google Sheet via URL
              </h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                View & share external CSV datasets or Google Sheets in Read-Only Mode
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-light)',
              cursor: 'pointer',
              padding: '0.4rem',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body Form */}
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: 'var(--text-main)',
              marginBottom: '0.5rem',
            }}>
              Public CSV File or Google Sheets URL
            </label>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}>
                <Link2 size={16} style={{ position: 'absolute', left: '0.85rem', color: 'var(--text-light)' }} />
                <input
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/... or https://...csv"
                  value={urlInput}
                  onChange={(e) => {
                    setUrlInput(e.target.value);
                    setErrorMessage(null);
                  }}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    padding: '0.65rem 0.85rem 0.65rem 2.5rem',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-main)',
                    fontSize: '0.9rem',
                    outline: 'none',
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handlePasteFromClipboard}
                disabled={isLoading}
                title="Paste URL from clipboard"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.65rem 0.85rem',
                  borderRadius: '10px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-main)',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <Clipboard size={15} />
                Paste
              </button>
            </div>

            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.4rem', lineHeight: 1.4 }}>
              Paste standard <strong>Google Sheets share links</strong>, published sheet URLs, GitHub raw CSV links, or any public CSV URL.
            </p>
          </div>

          {/* Error Notice */}
          {errorMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#ef4444',
              fontSize: '0.85rem',
              lineHeight: 1.4,
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>{errorMessage}</div>
            </div>
          )}

          {/* Read-Only Mode Note */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.65rem 0.85rem',
            borderRadius: '8px',
            background: 'var(--accent-glow)',
            color: 'var(--accent-primary)',
            fontSize: '0.8rem',
          }}>
            <Sparkles size={16} style={{ flexShrink: 0 }} />
            <span>URL datasets load in <strong>Read-Only Mode</strong>. Filters & views work as normal and can be shared via team URL.</span>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '0.6rem 1.1rem',
                borderRadius: '10px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-surface)',
                color: 'var(--text-main)',
                fontSize: '0.88rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isLoading || !urlInput.trim()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1.25rem',
                borderRadius: '10px',
                border: 'none',
                background: 'var(--accent-primary)',
                color: '#ffffff',
                fontSize: '0.88rem',
                fontWeight: 600,
                cursor: isLoading ? 'wait' : 'pointer',
                opacity: isLoading || !urlInput.trim() ? 0.7 : 1,
              }}
            >
              {isLoading ? 'Fetching CSV...' : 'Load & View CSV'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
